import { array, boolean, discriminatedUnion, enum as enumFactory, iso, literal, number, strictObject, string, tuple } from "../../lazy-zod.js";
import { centerAgentInteractionSourceSchema } from "../protocol/center-agent-interaction-source.js";
import { spaceMessageReactionsSchema } from "../space-mesh/message-reaction.js";
import { centerAgentSessionSelectorSchema } from "./center-agent-direct-message-space.js";
//#region src/schemas/http/center-agent-context-manifest.ts
const CENTER_AGENT_CONTEXT_MANIFEST_VERSION = "center_agent_context_manifest.v1";
const CENTER_AGENT_CONTEXT_LOAD_RECEIPT_VERSION = "center_agent_context_load_receipt.v1";
const CENTER_AGENT_CONTEXT_TOKEN_ESTIMATE_ALGORITHM = "utf8_bytes_divided_by_3.v1";
const CENTER_AGENT_CONTEXT_DEFAULT_MESSAGE_LIMIT = 20;
const CENTER_AGENT_CONTEXT_MAX_MESSAGE_LIMIT = 50;
const CENTER_AGENT_CONTEXT_DEFAULT_TOKEN_BUDGET = 8e3;
const CENTER_AGENT_CONTEXT_MIN_TOKEN_BUDGET = 256;
const CENTER_AGENT_CONTEXT_MAX_TOKEN_BUDGET = 32768;
const CENTER_AGENT_CONTEXT_ERROR_CODES = [
	"center_agent.context_conversation_required",
	"center_agent.context_cursor_invalid",
	"center_agent.context_token_budget_too_small",
	"center_agent.context_manifest_not_found",
	"center_agent.context_load_already_recorded",
	"center_agent.context_load_client_mismatch",
	"center_agent.context_load_manifest_empty",
	"center_agent.context_load_selection_duplicate",
	"center_agent.context_load_selection_invalid"
];
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const trimmedNonEmptyStringSchema = string().trim().min(1);
const contentHashSchema = string().regex(/^sha256:[0-9a-f]{64}$/u);
const centerAgentContextManifestIdSchema = string().regex(/^center_agent_context_manifest_[0-9a-f-]{36}$/u);
const loadReceiptIdSchema = string().regex(/^center_agent_context_load_receipt_[0-9a-f-]{36}$/u);
const centerAgentContextManifestViewSchema = literal("messages");
const createCenterAgentContextManifestBodySchema = strictObject({
	spaceId: centerAgentSessionSelectorSchema.shape.spaceId.optional(),
	cursor: string().trim().min(1).max(768).optional(),
	limit: number().int().min(1).max(50).default(20),
	maxContextTokens: number().int().min(256).max(CENTER_AGENT_CONTEXT_MAX_TOKEN_BUDGET).default(CENTER_AGENT_CONTEXT_DEFAULT_TOKEN_BUDGET),
	source: centerAgentInteractionSourceSchema,
	view: centerAgentContextManifestViewSchema.default("messages")
});
const centerAgentContextManifestSourceRefSchema = strictObject({
	id: trimmedNonEmptyStringSchema,
	type: literal("space_signal"),
	uri: string().regex(/^openmeld:\/\/spaces\/[^/]+\/signals\/[^/]+$/u),
	version: contentHashSchema
});
const centerAgentContextManifestEntrySchema = strictObject({
	reactions: spaceMessageReactionsSchema.optional(),
	author: strictObject({
		profileId: trimmedNonEmptyStringSchema,
		profileKind: enumFactory([
			"agent",
			"human",
			"system"
		]),
		profileName: trimmedNonEmptyStringSchema
	}),
	carrier: strictObject({
		client: trimmedNonEmptyStringSchema.max(80),
		kind: literal("local_agent")
	}).optional(),
	content: strictObject({
		originalCharacterCount: number().int().nonnegative(),
		text: string(),
		truncated: boolean(),
		type: literal("text")
	}),
	materializedContentHash: contentHashSchema,
	occurredAt: ISO_DATE_TIME_SCHEMA,
	redactions: array(enumFactory([
		"attachments_not_in_messages_view",
		"local_session_locator_omitted",
		"message_content_budget"
	])),
	replyToSignalId: trimmedNonEmptyStringSchema.optional(),
	sourceRef: centerAgentContextManifestSourceRefSchema
});
const centerAgentContextManifestMaterializationSchema = strictObject({
	estimatedTokens: number().int().nonnegative(),
	materializedContentHash: contentHashSchema,
	sourceRef: centerAgentContextManifestSourceRefSchema
});
const centerAgentContextLoadSelectedEntrySchema = strictObject({
	materializedContentHash: contentHashSchema,
	sourceRefId: trimmedNonEmptyStringSchema
});
const centerAgentContextLoadSelectionSchema = discriminatedUnion("kind", [strictObject({ kind: literal("all_manifest_entries") }), strictObject({
	entries: array(centerAgentContextLoadSelectedEntrySchema).min(1).max(50),
	kind: literal("selected_entries")
})]);
const createCenterAgentContextLoadReceiptBodySchema = strictObject({
	selection: centerAgentContextLoadSelectionSchema,
	source: centerAgentInteractionSourceSchema
});
const createCenterAgentContextManifestResponseSchema = strictObject({
	authorization: strictObject({
		principal: literal("authenticated_member"),
		scope: literal("requester_private")
	}),
	availableViews: tuple([literal("messages")]),
	budget: strictObject({
		estimateAlgorithm: literal(CENTER_AGENT_CONTEXT_TOKEN_ESTIMATE_ALGORITHM),
		estimatedTokens: number().int().nonnegative(),
		includedMessages: number().int().nonnegative(),
		requestedTokens: number().int().positive(),
		truncated: boolean(),
		truncationReasons: array(enumFactory([
			"context_token_budget",
			"message_content_budget",
			"message_limit",
			"older_messages_available"
		]))
	}),
	continuation: strictObject({
		cursor: string().trim().min(1).max(768).nullable(),
		hasMore: boolean()
	}),
	entries: array(centerAgentContextManifestEntrySchema),
	generatedAt: ISO_DATE_TIME_SCHEMA,
	manifestId: centerAgentContextManifestIdSchema,
	provenance: strictObject({
		auditEntryId: trimmedNonEmptyStringSchema,
		contentProjection: literal("user_visible_space_signals")
	}),
	request: strictObject({ source: centerAgentInteractionSourceSchema }),
	schemaVersion: literal(CENTER_AGENT_CONTEXT_MANIFEST_VERSION),
	subject: strictObject({
		centerAgentProfileId: trimmedNonEmptyStringSchema,
		conversationId: trimmedNonEmptyStringSchema,
		kind: literal("center_agent_conversation"),
		memberProfileId: trimmedNonEmptyStringSchema,
		organizationId: trimmedNonEmptyStringSchema
	}),
	view: centerAgentContextManifestViewSchema
});
const createCenterAgentContextLoadReceiptResponseSchema = strictObject({
	authorization: strictObject({
		principal: literal("authenticated_member"),
		scope: literal("requester_private")
	}),
	provenance: strictObject({ auditEntryId: trimmedNonEmptyStringSchema }),
	receipt: strictObject({
		client: trimmedNonEmptyStringSchema.max(80),
		estimatedTokens: number().int().nonnegative(),
		id: loadReceiptIdSchema,
		includedMessages: number().int().min(1).max(50),
		manifestId: centerAgentContextManifestIdSchema,
		selectedEntries: array(centerAgentContextLoadSelectedEntrySchema).min(1).max(50),
		status: literal("loaded")
	}),
	recordedAt: ISO_DATE_TIME_SCHEMA,
	schemaVersion: literal(CENTER_AGENT_CONTEXT_LOAD_RECEIPT_VERSION)
});
//#endregion
export { CENTER_AGENT_CONTEXT_DEFAULT_MESSAGE_LIMIT, CENTER_AGENT_CONTEXT_DEFAULT_TOKEN_BUDGET, CENTER_AGENT_CONTEXT_ERROR_CODES, CENTER_AGENT_CONTEXT_LOAD_RECEIPT_VERSION, CENTER_AGENT_CONTEXT_MANIFEST_VERSION, CENTER_AGENT_CONTEXT_MAX_MESSAGE_LIMIT, CENTER_AGENT_CONTEXT_MAX_TOKEN_BUDGET, CENTER_AGENT_CONTEXT_MIN_TOKEN_BUDGET, CENTER_AGENT_CONTEXT_TOKEN_ESTIMATE_ALGORITHM, centerAgentContextLoadSelectedEntrySchema, centerAgentContextLoadSelectionSchema, centerAgentContextManifestEntrySchema, centerAgentContextManifestIdSchema, centerAgentContextManifestMaterializationSchema, centerAgentContextManifestSourceRefSchema, centerAgentContextManifestViewSchema, createCenterAgentContextLoadReceiptBodySchema, createCenterAgentContextLoadReceiptResponseSchema, createCenterAgentContextManifestBodySchema, createCenterAgentContextManifestResponseSchema };

//# sourceMappingURL=center-agent-context-manifest.js.map